import { Fragment, useEffect, useMemo, useState } from 'react';

import { connect } from 'react-redux';

import PropTypes from 'prop-types';

import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';

import AddIcon from '@mui/icons-material/Add';

import { backendHOST } from '../../../../client-config/client-config';
import { logout } from '../../../../actions/auth';
import { PaymentMethods } from '../../../../utils/enums/userTypes';
import { withRouter } from '../../../hooks/withRouter';

const url = `${backendHOST}/api/scriptantiaAdmin`;
const urlEnd = '/CouponSell';

const emptyForm = {
  id_diak: '',
  datum: '',
  osszeg: '',
  descr: '',
  coupon_db: '',
  fiz_mod: '',
};

const FormCouponSell = ({ auth }) => {
  const [couponSells, setCouponSells] = useState([]);
  const [students, setStudents] = useState([]);

  const [searchText, setSearchText] = useState('');

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState(emptyForm);

  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);

  const canViewAllPayments = auth.user.permissions.includes('payments.view');

  const authHeaders = {
    'Content-Type': 'application/json',
    'x-auth-token': localStorage.token,
  };

  const formatDateForInput = (value) => {
    if (!value) return '';

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
      return '';
    }

    return date.toISOString().slice(0, 10);
  };

  const numberOrNull = (value) => {
    if (value === '' || value === null || value === undefined) {
      return null;
    }

    return Number(value);
  };

  const loadData = async () => {
    setLoading(true);
    setError(null);

    try {
      const couponSellParams = new URLSearchParams();

      if (!canViewAllPayments) {
        couponSellParams.set('createdBy', auth.user.ext_id_user);
      }

      const couponSellUrl = couponSellParams.toString()
        ? `${url}/CouponSell?${couponSellParams.toString()}`
        : `${url}/CouponSell`;

      const [couponSellResponse, studentsResponse] = await Promise.all([
        fetch(couponSellUrl, {
          headers: authHeaders,
        }),
        fetch(`${backendHOST}/api/auth/User?uType=D`, {
          headers: authHeaders,
        }),
      ]);

      if (!couponSellResponse.ok) {
        throw new Error('Nem sikerült betölteni a kupon értékesítéseket.');
      }

      if (!studentsResponse.ok) {
        throw new Error('Nem sikerült betölteni a diákokat.');
      }

      const couponSellData = await couponSellResponse.json();
      const studentsData = await studentsResponse.json();

      setCouponSells(Array.isArray(couponSellData) ? couponSellData : []);
      setStudents(Array.isArray(studentsData) ? studentsData : []);
    } catch (err) {
      setError(err.message || 'Ismeretlen hiba történt betöltés közben.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getStudentLabel = (studentId) => {
    const item = students.find(
      (student) => String(student.id) === String(studentId),
    );

    return item?.text || studentId || '';
  };

  const getPaymentMethodLabel = (paymentMethod) => {
    const item = PaymentMethods.find(
      (method) => String(method.key) === String(paymentMethod),
    );

    return item?.name || paymentMethod || '';
  };

  const sortedCouponSells = useMemo(() => {
    return [...couponSells].sort((a, b) => {
      const dateA = a.datum ? new Date(a.datum).getTime() : 0;
      const dateB = b.datum ? new Date(b.datum).getTime() : 0;

      return dateB - dateA;
    });
  }, [couponSells]);

  const filteredCouponSells = useMemo(() => {
    const normalizedSearch = searchText.trim().toLowerCase();

    if (!normalizedSearch) {
      return sortedCouponSells;
    }

    return sortedCouponSells.filter((item) =>
      [
        item.id_bizonylat,
        item.id_diak,
        getStudentLabel(item.id_diak),
        item.datum,
        item.osszeg,
        item.descr,
        item.coupon_db,
        item.fiz_mod,
        getPaymentMethodLabel(item.fiz_mod),
      ]
        .filter(Boolean)
        .some((value) =>
          String(value).toLowerCase().includes(normalizedSearch),
        ),
    );
  }, [sortedCouponSells, searchText, students]);

  const handleCreateClick = () => {
    setFormData({
      ...emptyForm,
      datum: formatDateForInput(new Date()),
      fiz_mod: PaymentMethods?.[0]?.key || '',
      coupon_db: '1',
    });

    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    if (saving) return;

    setDialogOpen(false);
    setFormData(emptyForm);
  };

  const handleFormChange = (field, value) => {
    setFormData((previous) => ({
      ...previous,
      [field]: value,
    }));
  };

  const normalizePayload = (data) => {
    return {
      id_diak: numberOrNull(data.id_diak),
      datum: data.datum || null,
      osszeg: numberOrNull(data.osszeg),
      descr: data.descr || '',
      coupon_db: numberOrNull(data.coupon_db),
      fiz_mod: data.fiz_mod || null,
    };
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);

    try {
      const payload = normalizePayload(formData);

      const response = await fetch(`${url}${urlEnd}`, {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error('Nem sikerült rögzíteni a kupon értékesítést.');
      }

      setMessage('Kupon értékesítés rögzítve.');

      setDialogOpen(false);
      setFormData(emptyForm);

      await loadData();
    } catch (err) {
      setError(err.message || 'Mentés közben hiba történt.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Fragment>
      <Box sx={{ p: 2 }}>
        <Card elevation={2} sx={{ borderRadius: 2 }}>
          <CardContent>
            <Stack spacing={2}>
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                alignItems={{ xs: 'stretch', md: 'center' }}
                justifyContent='space-between'
                spacing={2}
              >
                <Box>
                  <Typography variant='h6' sx={{ fontWeight: 600 }}>
                    Kupon értékesítés
                  </Typography>

                  <Typography variant='body2' color='text.secondary'>
                    Kuponcsomagok értékesítésének rögzítése diákokhoz.
                  </Typography>
                </Box>

                <Button
                  variant='contained'
                  startIcon={<AddIcon />}
                  onClick={handleCreateClick}
                >
                  Új kupon értékesítés
                </Button>
              </Stack>

              <TextField
                label='Keresés'
                size='small'
                value={searchText}
                onChange={(event) => setSearchText(event.target.value)}
                sx={{ maxWidth: 320 }}
              />

              {error && (
                <Alert severity='error' onClose={() => setError(null)}>
                  {error}
                </Alert>
              )}

              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <TableContainer component={Paper} variant='outlined'>
                  <Table size='small'>
                    <TableHead>
                      <TableRow>
                        <TableCell>Diák</TableCell>
                        <TableCell>Befizetés dátuma</TableCell>
                        <TableCell align='right'>Összeg</TableCell>
                        <TableCell>Megjegyzés</TableCell>
                        <TableCell align='right'>Kupon darabszám</TableCell>
                        <TableCell>Fizetési mód</TableCell>
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {filteredCouponSells.map((item) => (
                        <TableRow key={item.id_bizonylat} hover>
                          <TableCell>{getStudentLabel(item.id_diak)}</TableCell>
                          <TableCell>
                            {formatDateForInput(item.datum) || '-'}
                          </TableCell>
                          <TableCell align='right'>{item.osszeg}</TableCell>
                          <TableCell>{item.descr}</TableCell>
                          <TableCell align='right'>{item.coupon_db}</TableCell>
                          <TableCell>
                            {getPaymentMethodLabel(item.fiz_mod)}
                          </TableCell>
                        </TableRow>
                      ))}

                      {filteredCouponSells.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={6} align='center'>
                            Nincs megjeleníthető kupon értékesítés.
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Stack>
          </CardContent>
        </Card>
      </Box>

      <Dialog
        open={dialogOpen}
        onClose={handleDialogClose}
        fullWidth
        maxWidth='sm'
      >
        <DialogTitle>Új kupon értékesítés</DialogTitle>

        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <FormControl fullWidth>
              <InputLabel id='coupon-sell-student-label'>Diák</InputLabel>
              <Select
                labelId='coupon-sell-student-label'
                label='Diák'
                value={formData.id_diak}
                onChange={(event) =>
                  handleFormChange('id_diak', event.target.value)
                }
              >
                {students.map((student) => (
                  <MenuItem key={student.id} value={String(student.id)}>
                    {student.text}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label='Befizetés dátuma'
              type='date'
              value={formData.datum}
              onChange={(event) =>
                handleFormChange('datum', event.target.value)
              }
              InputLabelProps={{ shrink: true }}
              fullWidth
            />

            <TextField
              label='Összeg'
              type='number'
              value={formData.osszeg}
              onChange={(event) =>
                handleFormChange('osszeg', event.target.value)
              }
              fullWidth
            />

            <TextField
              label='Megjegyzés'
              value={formData.descr}
              onChange={(event) =>
                handleFormChange('descr', event.target.value)
              }
              multiline
              minRows={3}
              fullWidth
            />

            <TextField
              label='Kupon darabszám'
              type='number'
              value={formData.coupon_db}
              onChange={(event) =>
                handleFormChange('coupon_db', event.target.value)
              }
              fullWidth
            />

            <FormControl fullWidth>
              <InputLabel id='coupon-sell-payment-method-label'>
                Fizetési mód
              </InputLabel>
              <Select
                labelId='coupon-sell-payment-method-label'
                label='Fizetési mód'
                value={formData.fiz_mod}
                onChange={(event) =>
                  handleFormChange('fiz_mod', event.target.value)
                }
              >
                {PaymentMethods.map((method) => (
                  <MenuItem key={method.key} value={String(method.key)}>
                    {method.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleDialogClose} disabled={saving}>
            Mégsem
          </Button>

          <Button variant='contained' onClick={handleSave} disabled={saving}>
            {saving ? 'Mentés...' : 'Mentés'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={Boolean(message)}
        autoHideDuration={3000}
        onClose={() => setMessage(null)}
        message={message}
      />
    </Fragment>
  );
};

FormCouponSell.propTypes = {
  auth: PropTypes.object.isRequired,
};

const mapStateToProps = (state) => ({
  auth: state.auth,
});

export default connect(mapStateToProps, { logout })(withRouter(FormCouponSell));
